/**
 * useAnalytics — обёртка над `trackEvent` с автоматическим pre-payload.
 *
 * Решение Phase 2 (см. discrepancies_v1.md, расхождение №19):
 *   Поле `city` обязательно во ВСЕХ событиях аналитики — критично для
 *   разбиения воронки между 5 филиалами франшизы. Чтобы не дублировать
 *   `{ city: state.city }` в каждом вызове, используем хук, который
 *   читает city из SessionContext и подмешивает в каждое событие.
 *
 * Использование:
 *   const { track } = useAnalytics();
 *   track('start_test_click');                // → { city }
 *   track('manager_contact_click', { age_group }); // → { city, age_group }
 *
 * Если кастомный параметр явно содержит `city`, он перебивает default
 * (это нужно для редких случаев, когда трекаем для другого города,
 * например при переключении в DevToolbar).
 */

import { useCallback, useMemo } from 'react';
import { useSession } from '@/hooks/useSession';
import { trackEvent } from '@/lib/analytics';
import type { AnalyticsEventName, AnalyticsEventParams } from '@/types/analytics';

export function useAnalytics() {
  const { state } = useSession();

  const track = useCallback(
    (name: AnalyticsEventName, params?: AnalyticsEventParams) => {
      // city=null (real-mode без валидного start_param) → 'unknown', чтобы
      // events продолжали трекаться без падения. На /invalid-link редко
      // что-то трекаем, но useAnalytics может быть вызван outside guard.
      trackEvent(name, { city: state.city ?? 'unknown', ...(params ?? {}) });
    },
    [state.city],
  );

  return useMemo(() => ({ track }), [track]);
}
